import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, Divider, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/GetApp";
import moment from "moment";
import { isAxiosError } from "axios";
import FacturacionService from "../../../services/FacturacionService";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import DocUtils from "../../../utils/Doc";
import ConfigUtils from "../../../utils/Config";
import { formatCurrency } from "../../../utils/Factura";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import { IConcepto } from "../facturarCita/DetalleFactura";
import { chipEstadoFactura, chipEstadoPago } from "./chipsEstadoFactura";
import EnlaceFactura from "./EnlaceFactura";

interface VisorFacturaProps {
  facturaId: string;
  puedeVolver: boolean;
  onVolver: () => void;
  onClose: () => void;
}

// Visor de CONSULTA de una factura relacionada: solo lectura (sin pagar, emitir, anular, rectificar, eliminar ni cambiar
// entidad). Se distingue del detalle con acciones por la cabecera gris, el aviso fijo y un pie que solo permite volver o
// cerrar. Carga cualquier tipo de factura por su id con el endpoint genérico.
const VisorFactura = ({ facturaId, puedeVolver, onVolver, onClose }: VisorFacturaProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [factura, setFactura] = useState<IFacturaCompleta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandirDetalle, setExpandirDetalle] = useState(false);

  useEffect(() => {
    let cancelado = false;
    const cargar = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await FacturacionService.getFacturaManualById(facturaId);
        if (!cancelado) setFactura(response.data.factura);
      } catch (e: unknown) {
        if (cancelado) return;
        const respuesta = isAxiosError(e) ? e.response : undefined;
        if (respuesta?.status === 404) {
          setError("Esta factura ya no existe");
        } else {
          const log: ILog = { method: "VisorFactura.Component.load", level: "error", message: e instanceof Error ? e.message : String(e), meta: respuesta };
          LogService.logError(log);
          setError(respuesta?.data?.message || "No se ha podido cargar la factura");
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    cargar();
    return () => { cancelado = true; };
  }, [facturaId]);

  const moneda = factura?.moneda || "EUR";

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <Box
        sx={{
          px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1,
          backgroundColor: "grey.200", borderBottom: "1px solid", borderColor: "divider",
        }}
      >
        <VisibilityIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
          Consulta{factura?.numero ? ` · Factura nº ${factura.numero}` : factura ? " · Borrador" : ""}
        </Typography>
        <Chip size="small" variant="outlined" label="Solo lectura" />
      </Box>

      <Alert severity="info" icon={false} sx={{ borderRadius: 0, py: 0.25, fontSize: "0.8rem" }}>
        Vista de consulta: no se puede modificar desde aquí
      </Alert>

      {loading && <CircularProgress style={{ margin: "24px auto" }} />}
      {!loading && error && <Alert severity="warning" sx={{ m: 2 }}>{error}</Alert>}

      {!loading && factura && (
        <DialogContent>
          {factura.emisor && ConfigUtils.getDatosFacturacionAdicional().length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <b>Entidad emisora:</b> {factura.emisor.razonSocial}
            </Typography>
          )}
          <Typography variant="body2"><b>Receptor:</b> {factura.receptor.nombre}{factura.receptor.nif ? ` (${factura.receptor.nif})` : ""}</Typography>

          <Divider sx={{ my: 1.5 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Información de la factura</Typography>
            <Tooltip title="Descargar factura">
              <IconButton size="small" onClick={() => DocUtils.formatFactura(factura, true, "total")}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 3} alignItems={isMobile ? "flex-start" : "center"}>
            <Stack direction="row" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Nº</Typography>
              <Typography variant="body2">{factura.numero || "Sin número"}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Fecha</Typography>
              <Typography variant="body2">{new Date(factura.fechaFactura).toLocaleDateString()}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Estado</Typography>
              {factura.estado === "Anulada" ? (
                <Tooltip title={factura.motivoAnulacion ?? ""} placement="top" arrow>
                  <Chip label={factura.estado} size="small" sx={{ border: "1px solid #d32f2f", color: "#d32f2f" }} />
                </Tooltip>
              ) : (
                <Chip {...chipEstadoFactura(factura)} size="small" />
              )}
            </Stack>
          </Stack>

          {(factura.facturaOriginalId || factura.facturaRectificadaId) && (
            <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
              {factura.facturaOriginalId && (
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    {factura.tipoRectificacion === "anulacion" ? "Anulación de la factura nº" : "Rectificativa de la factura nº"}
                  </Typography>
                  <EnlaceFactura facturaId={factura.facturaOriginalRef} numero={factura.facturaOriginalId} cursiva />
                </Stack>
              )}
              {factura.facturaRectificadaId && (
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    {factura.estado === "Anulada" ? "Anulada por la factura nº" : "Rectificada por la factura nº"}
                  </Typography>
                  <EnlaceFactura facturaId={factura.facturaRectificadaId} numero={factura.facturaRectificada} cursiva />
                </Stack>
              )}
            </Stack>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
            <Box display="flex" flexDirection="column" gap={0.75}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Cobro</Typography>
                <Chip {...chipEstadoPago(factura)} size="small" />
              </Box>
              <Typography variant="body2"><b>Servicio:</b> {factura.servicio}</Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary">Importe</Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>{formatCurrency(Number(factura.importeTotal), moneda)}</Typography>
            </Box>
          </Box>

          {factura.estadoPago === "Pagada" && factura.metodoPago && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>Método de pago:</b> {factura.metodoPago.nombre} · <b>Fecha de pago:</b> {moment(factura.metodoPago.fechaPago).format("DD/MM/YYYY")}
            </Typography>
          )}

          <Button variant="outlined" size="small" onClick={() => setExpandirDetalle(!expandirDetalle)}>
            {expandirDetalle ? "Ocultar detalles" : "Ver detalles"}
          </Button>
          <Collapse in={expandirDetalle}>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Concepto</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Precio unit.</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {factura.conceptos.map((c: IConcepto, index: number) => {
                  const imp = factura.impuestos?.[index];
                  const base = c.cantidad * c.precio;
                  const cuota = imp ? base * (imp.cuota / 100) : 0;
                  return (
                    <React.Fragment key={index}>
                      <TableRow hover>
                        <TableCell>{c.nombre}</TableCell>
                        <TableCell align="right">{c.cantidad}</TableCell>
                        <TableCell align="right">{formatCurrency(c.precio, moneda)}</TableCell>
                        <TableCell align="right">{formatCurrency(base, moneda)}</TableCell>
                      </TableRow>
                      {imp && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ pl: 6, py: 0.8, backgroundColor: "action.hover", borderBottom: "none" }}>
                            <Typography variant="body2" color="text.secondary">
                              {imp.tipo} ({imp.cuota}%): {formatCurrency(cuota, moneda)}
                              &nbsp;•&nbsp;
                              <strong>Total: {formatCurrency(base + cuota, moneda)}</strong>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </DialogContent>
      )}

      <DialogActions sx={{ backgroundColor: "grey.100" }}>
        {puedeVolver && <Button onClick={onVolver}>Volver</Button>}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisorFactura;
